/* eslint-disable react/jsx-no-target-blank */
/* eslint-disable @next/next/no-img-element */
import React, { FC, useContext, useState } from "react";
import { GetServerSideProps } from "next";
import Link from "next/link";
import RockstarFooter from "components/RockstarFooter";
import RockstarHeader from "components/RockstarHeader";
import { endpoints } from "config";
import HeadTagsRenderer from "components/HeadTagsRenderer";
import { WpYoastHead } from "types/wp-yoast-head";
import { LogContext } from "context/log";
import { AthleteFragment, getSdk } from "services/datocms/generated";
import { createDatoClient } from "services/datocms";
import RockstarRelatedProducts from "components/RockstarRelatedProducts";
import TopCtaBanner from "components/TopCtaBanner";
import { generatePageId } from "@pepdirect/helpers/analyticsLogger";
import { PAGE_TYPE_ENUM } from "@pepdirect/shared/types";
import { Button } from "@pepdirect/ui/button";

interface AthletesPageProps {
  athletes: AthleteFragment[];
}

const ATHLETE_FILTER_MENU_OPTIONS = [
  "All",
  "AUTO",
  "BIKE",
  "BMX",
  "FMX",
  "MOTO",
  "SKATE",
  "SNOW",
  "SURF",
];

export const getServerSideProps: GetServerSideProps<AthletesPageProps> = async (
  context
) => {
  try {
    const { allAthletes } = await getSdk(
      createDatoClient(context.preview)
    ).AllAthletes();

    return {
      props: {
        athletes: allAthletes,
      },
    };
  } catch (e) {
    return {
      props: {
        athletes: [],
      },
    };
  }
};

const Athletes: FC<AthletesPageProps> = ({ athletes }) => {
  const { useLogPageView } = useContext(LogContext);

  const [activeMenuOption, setActiveMenuOption] = useState<string>("All");

  const seoTags: WpYoastHead = {
    title: "Athlete Posts Archive - Rockstar Energy",
    canonical: `href="${endpoints.web}/athletes/`,
    og_locale: "en_US",
    og_type: "website",
    og_title: "Athlete Posts Archive - Rockstar Energy",
    og_url: `${endpoints.web}/athletes/`,
    og_site_name: "Rockstar Energy",
    twitter_card: "summary_large_image",
  };

  useLogPageView({
    pageType: PAGE_TYPE_ENUM.other,
    pageCategory: undefined,
    pageTitle: seoTags.title || "",
    id: generatePageId("athletes"),
  });

  return (
    <>
      <HeadTagsRenderer yoastJson={seoTags} />
      <div id="page" className="site">
        <a className="skip-link screen-reader-text" href="#content">
          Skip to content
        </a>

        <div className="site-header-block">
          <TopCtaBanner />
          <RockstarHeader />
        </div>

        <div id="content" className="site-content">
          <div id="primary" className="content-area">
            <main id="main" className="site-main" role="main">
              <div className="athletes-page">
                <div className="container has-text-align-center entry-header alignwide">
                  <h1 className="entry-title">
                    REFRESHED ROSTER COMING IN 2025
                  </h1>
                </div>
                <div
                  className="custom-post-with-filter"
                  id="custom-post-with-filter ?>"
                >
                  <div className="container">
                    <div className="filter-list focus">
                      <ul>
                        {ATHLETE_FILTER_MENU_OPTIONS.map((text) => {
                          return (
                            <li
                              className={
                                text === activeMenuOption ? "active-filter" : ""
                              }
                              key={text}
                              onMouseEnter={() => {
                                setActiveMenuOption(text);
                              }}
                            >
                              <a
                                href="javascript:void(0);"
                                title=""
                                data-filter={text.toLowerCase()}
                              >
                                {text}
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                    <div className="filter-custom-contents">
                      {athletes
                        .sort(
                          (a, b) =>
                            a.athleteType?.localeCompare(
                              b?.athleteType || ""
                            ) || (a?.title || "").localeCompare(b?.title || "")
                        )
                        ?.filter((athlete) => {
                          return (
                            activeMenuOption === "All" ||
                            athlete.athleteType === activeMenuOption ||
                            (activeMenuOption === "MOTO" &&
                              athlete.athleteType === "MX")
                          );
                        })
                        ?.map((athlete) => {
                          return (
                            <div
                              key={athlete?.id}
                              className={`filter-custom-content all active-info ${
                                athlete?.athleteType === "MX"
                                  ? "moto"
                                  : athlete?.athleteType?.toLocaleLowerCase() ||
                                    ""
                              }`}
                            >
                              <div className="filter-custom-content-wrapper">
                                <div className="feature-title-block focus">
                                  <span>{athlete?.athleteType}</span>
                                  <h3>
                                    <Link
                                      href={`/athletes/${athlete?.slug || ""}`}
                                      title=""
                                    >
                                      {athlete?.title || ""}
                                    </Link>
                                  </h3>
                                </div>
                                <Link
                                  href={`/athletes/${athlete?.slug || ""}`}
                                  title=""
                                  className="feature-img-wrapper"
                                >
                                  <div
                                    className="feature-img"
                                    style={{
                                      backgroundImage: `url(${
                                        athlete?.athleteImage?.url || ""
                                      })`,
                                    }}
                                  ></div>
                                </Link>
                                <a
                                  href={
                                    athlete?.socialMediaLink?.[0]?.url || ""
                                  }
                                  title={
                                    athlete?.socialMediaLink?.[0]?.title || ""
                                  }
                                  target="_blank"
                                  className="name-tag focus"
                                >
                                  {athlete?.socialMediaLink?.[0]?.title || ""}
                                </a>{" "}
                                <div className="feature-item-details">
                                  <Button
                                    internalLink={`/athletes/${
                                      athlete?.slug || ""
                                    }`}
                                    title=""
                                    className="btn feature-btn focus-text"
                                  >
                                    See More
                                  </Button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
                <RockstarRelatedProducts />
              </div>
            </main>
          </div>
        </div>

        <RockstarFooter />
      </div>
    </>
  );
};

export default Athletes;
